import { Decoder, Encoder, Stream } from "@garmin/fitsdk";
import type { EncoderOptions, Mesg } from "@garmin/fitsdk";
import { MesgNum } from "./decode";
import { boundariesToSegments, type LapSegment } from "./laps";
import type { OrderedMesg, RideData, RecordPoint } from "./types";

const SEMICIRCLE_TO_DEG = 180 / 2 ** 31;

function degToSemicircles(deg: number | null): number | null {
  return deg === null ? null : Math.round(deg / SEMICIRCLE_TO_DEG);
}

/** Build a minimal, valid LAP message for a segment (boundaries + basics). */
function buildLapMesg(
  records: RecordPoint[],
  seg: LapSegment,
  ride: RideData,
  isLast: boolean,
): Record<string, unknown> {
  const start = records[seg.startIdx];
  const end = records[seg.endIdx];

  const mesg: Record<string, unknown> = {
    messageIndex: seg.index,
    timestamp: new Date(seg.endTime),
    startTime: new Date(seg.startTime),
    totalElapsedTime: seg.durationSec,
    // "Basics": leave moving/timer time equal to elapsed and let Garmin/Strava
    // recompute the detailed metrics on import.
    totalTimerTime: seg.durationSec,
    totalMovingTime: seg.durationSec,
    event: "lap",
    eventType: "stop",
    lapTrigger: isLast ? (ride.lastLapTrigger ?? "sessionEnd") : "manual",
    developerFields: {},
  };

  if (seg.distanceM !== null) mesg.totalDistance = seg.distanceM;

  const startLat = degToSemicircles(start.lat);
  const startLng = degToSemicircles(start.lng);
  const endLat = degToSemicircles(end.lat);
  const endLng = degToSemicircles(end.lng);
  if (startLat !== null && startLng !== null) {
    mesg.startPositionLat = startLat;
    mesg.startPositionLong = startLng;
  }
  if (endLat !== null && endLng !== null) {
    mesg.endPositionLat = endLat;
    mesg.endPositionLong = endLng;
  }
  if (ride.sport !== undefined) mesg.sport = ride.sport;
  if (ride.subSport !== undefined) mesg.subSport = ride.subSport;

  return mesg;
}

/**
 * Whether a decoded message has any field the Encoder can write. Some real
 * files contain messages whose fields are all unknown to the profile; the
 * decoder drops those, leaving an empty object the Encoder rejects with
 * "No valid fields were found in the message". Such messages carry no data,
 * so they are safe to skip on re-encode.
 */
function hasWritableFields(mesg: Record<string, unknown>): boolean {
  const dev = mesg.developerFields as Record<string, unknown> | undefined;
  if (dev && Object.keys(dev).length > 0) return true;
  for (const k of Object.keys(mesg)) {
    if (k === "developerFields") continue;
    const v = mesg[k];
    if (v === undefined || v === null) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    return true;
  }
  return false;
}

/** Count how many of the rebuilt laps start within a session's time window. */
function lapsInSession(
  session: Record<string, unknown>,
  segments: LapSegment[],
): number {
  const s = session.startTime instanceof Date ? session.startTime.getTime() : null;
  const e = session.timestamp instanceof Date ? session.timestamp.getTime() : null;
  if (s === null || e === null) return segments.length;
  return segments.filter((seg) => seg.startTime >= s && seg.startTime <= e)
    .length;
}

/**
 * Re-encode the ride with laps rebuilt from `splits` (internal boundary record
 * indices). Preserves the original message stream and developer fields; only
 * lap messages are replaced and each session's `numLaps` is updated.
 */
export function encodeFit(ride: RideData, splits: number[]): Uint8Array {
  const segments = boundariesToSegments(ride.records, splits);

  // Build the replacement lap messages, indexed by their end time for placement.
  const lapByEndTime = new Map<number, OrderedMesg[]>();
  segments.forEach((seg, i) => {
    const mesg = buildLapMesg(
      ride.records,
      seg,
      ride,
      i === segments.length - 1,
    );
    const entry: OrderedMesg = { mesgNum: MesgNum.LAP, mesg };
    const bucket = lapByEndTime.get(seg.endTime);
    if (bucket) bucket.push(entry);
    else lapByEndTime.set(seg.endTime, [entry]);
  });

  // Rebuild the ordered stream: drop old laps, update session numLaps, and
  // re-insert each new lap right after the record that marks its end.
  const placed = new Set<number>();
  const rebuilt: OrderedMesg[] = [];
  for (const item of ride.ordered) {
    if (item.mesgNum === MesgNum.LAP) continue; // replaced below
    if (!hasWritableFields(item.mesg)) continue; // skip fieldless messages
    if (item.mesgNum === MesgNum.SESSION) {
      item.mesg.numLaps = lapsInSession(item.mesg, segments);
    }
    rebuilt.push(item);

    if (item.mesgNum === MesgNum.RECORD && item.mesg.timestamp instanceof Date) {
      const t = item.mesg.timestamp.getTime();
      const laps = lapByEndTime.get(t);
      if (laps && !placed.has(t)) {
        rebuilt.push(...laps);
        placed.add(t);
      }
    }
  }

  // Fallback: any lap whose end record wasn't matched goes before the first
  // session message (or at the end if there is none).
  const unplaced: OrderedMesg[] = [];
  for (const [t, laps] of lapByEndTime) {
    if (!placed.has(t)) unplaced.push(...laps);
  }
  if (unplaced.length > 0) {
    const sessionPos = rebuilt.findIndex((i) => i.mesgNum === MesgNum.SESSION);
    if (sessionPos === -1) rebuilt.push(...unplaced);
    else rebuilt.splice(sessionPos, 0, ...unplaced);
  }

  const encoder = new Encoder({
    // Keyed by field key at runtime; the SDK's typing is stricter than reality.
    fieldDescriptions: ride.fieldDescriptions,
  } as unknown as EncoderOptions);
  for (const { mesgNum, mesg } of rebuilt) {
    encoder.onMesg(mesgNum, mesg as unknown as Mesg);
  }
  return encoder.close();
}

export interface ValidationResult {
  ok: boolean;
  lapCount: number;
  recordCount: number;
  message: string;
}

/** Re-decode encoded bytes to confirm the output is a valid FIT file. */
export function validateFit(
  bytes: Uint8Array,
  expectedLaps: number,
): ValidationResult {
  try {
    const stream = Stream.fromByteArray(bytes);
    const decoder = new Decoder(stream);
    if (!decoder.isFIT() || !decoder.checkIntegrity()) {
      return {
        ok: false,
        lapCount: 0,
        recordCount: 0,
        message: "Output failed the FIT integrity check.",
      };
    }
    const { messages, errors } = decoder.read();
    const lapCount = (messages.lapMesgs ?? []).length;
    const recordCount = (messages.recordMesgs ?? []).length;
    if (errors.length > 0) {
      return {
        ok: false,
        lapCount,
        recordCount,
        message: errors[0].message || "Output re-decode reported errors.",
      };
    }
    if (lapCount !== expectedLaps) {
      return {
        ok: false,
        lapCount,
        recordCount,
        message: `Expected ${expectedLaps} laps but the output has ${lapCount}.`,
      };
    }
    return {
      ok: true,
      lapCount,
      recordCount,
      message: "Validated.",
    };
  } catch (err) {
    return {
      ok: false,
      lapCount: 0,
      recordCount: 0,
      message: err instanceof Error ? err.message : "Validation failed.",
    };
  }
}
